import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TelXLPanelComponent } from '@dxp/elements';
import { ActivatedRoute } from '@angular/router';
import { map, Observable, tap } from 'rxjs';
import { MonitoringService } from '@dxp/core/services';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { CustomerInteractionsComponent } from '@dxp/customer-interactions';
import { IWorkItem } from '@dxp/core/models';
import { WORKITEMS_STORE } from '@dxp/core/stores';
import { UnknownCustomer } from '@dxp/core/types';
import { VoiceMonitorCallComponent } from '../voice-monitor-call/voice-monitor-call.component';

@Component({
    selector: 'voice-monitoring-container',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        TelXLPanelComponent,
        VoiceMonitorCallComponent,
        CustomerInteractionsComponent,
    ],
    templateUrl: './voice-monitoring-container.component.html',
    styleUrl: './voice-monitoring-container.component.scss',
})
export class VoiceMonitoringContainerComponent implements OnInit {
    readonly #activatedRoute = inject(ActivatedRoute);
    protected monitoringService = inject(MonitoringService);
    protected workItemStorev2 = inject(WORKITEMS_STORE);

    /**
     * Holds the active state for the current task
     */
    taskActive = signal<boolean>(true);

    /**
     * Updates when the monitoring task has ended
     */
    callEnded = signal({
        endedAt: new Date(),
        startedAt: new Date(),
    });

    startedAt = new Date();

    params$: Observable<{ conversationId: string }> =
        this.#activatedRoute.paramMap.pipe(
            tap(params => {
                /**
                 * Update the workItem Id
                 * @description Currently the workItem Id is set in the communication service but not consistently - Until it is - leave the update here
                 */
                this.workItemStorev2.updateActiveWorkId(
                    params.get('workItemId') ?? '',
                );

                /**
                 * Update the conversation Id
                 * @description Currently the workItem Id is set in the communication service but not consistently - Until it is - leave the update here
                 */
                this.workItemStorev2.updateActiveConversationId(
                    params.get('conversationId') ?? '',
                );
            }),
            map(params => ({
                conversationId: params.get('conversationId') ?? '',
                workItemId: params.get('workItemId') ?? '',
            })),
        );

    #routerParams = toSignal(this.params$);

    workItem = computed<IWorkItem>(() => {
        const conversationId = this.#routerParams()?.conversationId || '';
        const conversations =
            this.monitoringService.activeMonitoredConversations();
        const activeConversation = conversations[conversationId];

        return activeConversation
            ? activeConversation
            : {
                  conversations: [],
                  createdAt: new Date(),
                  customerId: null,
                  businessUnitId: '',
                  displayCustomer: UnknownCustomer,
                  channelId: 0,
                  workItemId: '',
                  workItemState: 'Connected',
                  wrapupDurationSeconds: 0,
                  lastStateChangeDate: new Date(),
                  primaryChannelType: 'voice',
                  primaryConversationId: '',
                  primaryMediaType: 'Voice',
                  remainingWrapUpResets: 0,
                  usableClosureCodes: [],
                  enableChatEmojis: false,
              };
    });

    /**
     * Turn the active Work Item into an observable
     * Allowing us to listen to when state changes and a conversation no longer exists
     *
     * Once the conversation has been removed - Update the signals
     */
    activeWorkItem$ = toObservable(this.workItemStorev2.activeWorkItem).pipe(
        takeUntilDestroyed(),
        //filter(workItem => workItem !== null),
        tap(workItem => {
            if (!workItem) {
                this.taskActive.set(false);

                /**
                 * Reset the button to be active once the call has ended
                 */
                this.monitoringService.enableButton();

                this.callEnded.set({
                    endedAt: new Date(),
                    startedAt: this.startedAt,
                });
                return;
            }
            this.startedAt = new Date(workItem.createdAt);
            /**
             * If the conversation no longer exists
             */
            if (workItem.conversations.length === 0) {
                /**
                 * Reset the button to be active once the call has ended
                 */
                this.monitoringService.enableButton();

                /**
                 * Update the task active to a false value
                 */
                this.taskActive.set(false);

                /**
                 * Pass the start and end monitoring time
                 */
                this.callEnded.set({
                    endedAt: new Date(workItem.lastStateChangeDate),
                    startedAt: new Date(workItem.createdAt),
                });
            }
        }),
    );

    ngOnInit(): void {
        this.activeWorkItem$.subscribe();
    }
}
